import React from 'react';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import merge from 'lodash.merge';
import createStyles from '@material-ui/core/styles/createStyles';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import colors from 'components/PortfolioTables/colors';
import useMediaQuery from '@material-ui/core/useMediaQuery';

interface Props {
  data: PieSeriesData;
  seriesName?: string;
  settings?: Highcharts.Options;
  title?: string;
}

export interface PieData {
  name: string;
  y: number;
  sliced?: boolean;
  selected?: boolean;
  color: any;
}

export interface PieSeriesData {
  portfolioColor?: string | null;
  title?: string;
  subtitle?: string;
  data?: PieData[];
}

const useStyles = makeStyles(() =>
  createStyles({
    fixChart: {
      margin: '0 auto',
      maxWidth: '800px',
      '@media (max-width: 1023px)': {
        maxWidth: '640px',
      },
      '& .highcharts-container': {
        margin: '0 auto',
        width: '100%',
        '& svg': {
          width: '100%',
        },
      },
      '& .highcharts-tooltip path': {
        display: 'none',
      },
      '& .highcharts-legend-item > span': {
        fontFamily: `"Roboto", "Helvetica", "Arial", sans-serif !important`,
      },
      '& ._tooltip': {
        zIndex: 1000,
        '& > span > span': {
          background: 'rgba(255, 255, 255, 0.9)',
          padding: '8px',
          display: 'inline-block',
          fontSize: '0.8em',
          textAlign: 'center',
          boxSizing: 'border-box',
          borderRadius: '10px',
          border: 'solid 2px #000',
          '&.IK1': { color: colors.IK1, borderColor: colors.IK1 },
          '&.IK2': { color: colors.IK2, borderColor: colors.IK2 },
          '&.IK3': { color: colors.IK3, borderColor: colors.IK3 },
          '&.IK4': { color: colors.IK4, borderColor: colors.IK4 },
          '&.IK5': { color: colors.IK5, borderColor: colors.IK5 },
        },
      },
    },
  }),
);

const titleFormatter = (title: string, subtitle: string, portfolioColor: string, mobile?: boolean) => {
  const addon = mobile
    ? ''
    : `<br /><span style="display: inline-block; margin-top: 4px; font-size: 14px; color: #000; font-weight: 900; white-space: normal">${subtitle.replace(
        /\s/,
        '<br />',
      )}</span>`;
  return `<div style="text-align: center; font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif; font-size: 22px; font-weight: 700; color: ${portfolioColor}">${title
    .toUpperCase()
    .replace(/\s/g, '<br />')}${addon}</div>`;
};

const PieGraph: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const tablet = useMediaQuery('@media (max-width: 1023px)', { noSsr: true });
  const mobile = useMediaQuery('@media (max-width: 639px)', { noSsr: true });

  const defaultSettings: Highcharts.Options = {
    chart: {
      type: 'pie',
      spacingTop: 0,
    },
    credits: {
      enabled: false,
    },
    title: {
      text: titleFormatter(
        props.title || props.data.title || '',
        props.data.subtitle || '',
        props.data.portfolioColor || '',
      ),
      verticalAlign: 'middle',
      useHTML: true,
      style: {
        zIndex: 10,
      },
    },
    tooltip: {
      borderRadius: 20,
      borderWidth: 2,
      shadow: false,
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      useHTML: true,
      className: '_tooltip',
      formatter: function (this) {
        return `<span class="${this.point.name}">${t(this.point.name)
          .toUpperCase()
          .replace(' ', '<br />')}<br /><span style="font-size: 1.5em; font-weight: bold">${this.y} %</span></span>`;
      },
      style: {
        zIndex: 15,
      },
    },
    plotOptions: {
      pie: {
        allowPointSelect: false,
        point: {
          events: {
            legendItemClick: function () {
              return false;
            },
          },
        },
        dataLabels: {
          enabled: false,
        },
        showInLegend: true,

        size: '99%',
      },
    },
    legend: {
      align: 'right',
      layout: 'vertical',
      verticalAlign: 'bottom',
      symbolWidth: 16,
      symbolHeight: 16,
      symbolRadius: 16,
      itemMarginBottom: 8,
      itemStyle: { lineHeight: '10px' },
      floating: true,
      useHTML: true,
      labelFormatter: function () {
        return t(this.name);
      },
      x: 10,
    },

    series: [
      {
        id: 'main',
        name: props.seriesName || '',
        type: 'pie',
        data: props.data.data,
        slicedOffset: 0,
        dataLabels: {
          connectorColor: '#ccc',
          connectorShape: 'fixedOffset',
          softConnector: false,
          connectorWidth: 2,
          useHTML: true,
          formatter: function (this) {
            return `${t(this.point.name).toUpperCase().replace(' ', '<br />')}`;
          },
        },
        innerSize: '70%',
      },
      {
        id: 'shadow',
        name: props.seriesName || '',
        type: 'pie',
        data: props.data.data ? props.data.data.map((d) => ({ name: d.name, y: d.y, color: colors[d.color] })) : [],
        shadow: false,
        borderColor: 'transparent',
        borderWidth: 3,
        showInLegend: false,
        slicedOffset: 0,
        dataLabels: {
          enabled: false,
        },
        enableMouseTracking: false,

        innerSize: '85%',
        size: '70%',
      },
    ],
    responsive: {
      rules: [
        {
          condition: { callback: () => tablet },
          chartOptions: {
            plotOptions: {
              pie: {
                size: '90%',
              },
            },
            legend: {
              x: 10,
            },
          },
        },
        {
          condition: { callback: () => mobile },
          chartOptions: {
            plotOptions: {
              pie: {
                size: '90%',
              },
            },
            chart: {
              spacingRight: 0,
              height: 500,
              spacingBottom: 80,
            },
            legend: {
              align: 'left',
              floating: true,
              y: 80,
              x: 20,
            },
            series: [
              { id: 'main', innerSize: '60%', type: 'pie' },
              { id: 'shadow', size: '60%', innerSize: '85%', type: 'pie' },
            ],
          },
        },
      ],
    },
  };

  const graphSettings = props.settings ? merge({}, defaultSettings, props.settings) : defaultSettings;

  return (
    <div className={classes.fixChart}>
      <HighchartsReact highcharts={Highcharts} options={graphSettings} />
    </div>
  );
};

export default React.memo(PieGraph);
